import { SPROUT_JUDGE_SCORER_ID } from './threadScorers';

export interface ScorerRubricBand {
    /** Inclusive, 0–100. Matches the % Grove shows. */
    minPercent: number;
    /** Inclusive, 0–100. */
    maxPercent: number;
    summary: string;
}

export interface ScorerRubricTooltipRow {
    label: string;
    value: string;
}

/**
 * Published scoring brackets for Grove tooltips. Ranges match the judge
 * prompt in `sproutJudgeScorer.ts`. Summaries are the generic band names only
 * — Sprout-specific examples stay in the score reason, not here.
 */
export const SCORER_RUBRIC_BANDS: Readonly<
    Record<string, readonly ScorerRubricBand[]>
> = {
    [SPROUT_JUDGE_SCORER_ID]: [
        {
            minPercent: 90,
            maxPercent: 100,
            summary: 'Fully correct',
        },
        {
            minPercent: 70,
            maxPercent: 89,
            summary: 'Correct overall with minor issues',
        },
        {
            minPercent: 40,
            maxPercent: 69,
            summary: 'Partially correct',
        },
        {
            minPercent: 10,
            maxPercent: 39,
            summary: 'Wrong approach',
        },
        {
            minPercent: 0,
            maxPercent: 9,
            summary: 'Harmful or out of bounds',
        },
    ],
};

export const getScorerRubricBands = (
    scorerId: string,
): readonly ScorerRubricBand[] | null => {
    switch (scorerId) {
        case SPROUT_JUDGE_SCORER_ID:
            return SCORER_RUBRIC_BANDS[SPROUT_JUDGE_SCORER_ID] ?? null;
        default:
            return null;
    }
};

/** `score` is the stored 0–1 value; compared to bands via the displayed %. */
export const getScorerRubricBandForScore = (
    scorerId: string,
    score: number,
): ScorerRubricBand | null => {
    const bands = getScorerRubricBands(scorerId);
    if (bands == null) {
        return null;
    }

    const percent = Math.round(score * 100);
    return (
        bands.find(
            (band) => percent >= band.minPercent && percent <= band.maxPercent,
        ) ?? null
    );
};

export const getScorerRubricTooltipRows = (
    scorerId: string,
): ScorerRubricTooltipRow[] | null => {
    const bands = getScorerRubricBands(scorerId);
    if (bands == null) {
        return null;
    }

    return bands.map((band) => ({
        label: `${band.minPercent.toString()}–${band.maxPercent.toString()}`,
        value: band.summary,
    }));
};
